import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { focusElement } from '../../utils/useFocusOnOpen';
import IntakeForm from '../intake/IntakeForm';

/*
 * A new isolate intake from Home: the whole intake form (IntakeForm.js) in
 * the hub's side drawer, wider than a quick action's: taking something in
 * never leaves the hub (there is no External page any more).
 *
 * The drawer is QuickActionDrawer's: a labelled modal dialog over a
 * backdrop, its heading focused as it opens, Escape or × to close. The media
 * list the plate and the tubes choose from is read each time it opens, so a
 * medium typed as "Other" on the last intake is offered on the next.
 *
 * While the intake is being sent, Escape and × do nothing (× is disabled and
 * says why): the request is on its way, and closing would only lose its
 * answer. Otherwise they close through the form (IntakeForm's `ref`), so an
 * intake already saved with its tubes refused closes with that said, as the
 * form's own "Close without the tubes" does, rather than silently.
 *
 * Props:
 *   open     whether the drawer is shown
 *   user     the signed-in user (IntakeForm's `user`)
 *   onDone({ external_id, specimen, sourceType, warnings })
 *            the intake was made -- IntakeForm's onDone, as it gives it: the
 *            server's isolate (or null), and anything that failed after the
 *            record existed. The caller closes the drawer and refreshes.
 *   onClose  closes it without saving
 *   onSettled  optional: IntakeForm's -- a submission that wrote something
 *            finished, even if the drawer was closed meanwhile
 */
export default function IntakeDrawer({ open, user, onDone, onClose, onSettled }) {
  const { t } = useTranslation(['isolates', 'external', 'common']);
  const [protocols, setProtocols] = useState([]);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const formRef = useRef(null);
  const headingRef = useRef(null);
  const onBusyChange = (value) => {
    busyRef.current = value;
    setBusy(value);
  };

  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    api.get('/protocols')
      .then((res) => { if (live) setProtocols(Array.isArray(res?.data) ? res.data : []); })
      .catch(() => {});
    return () => { live = false; };
  }, [open]);

  // The heading takes focus once, as the drawer opens -- not again whenever
  // the hub re-renders with a new onClose, which would pull focus out of a
  // field being typed in. Escape reaches the latest onClose through a ref.
  const closeRef = useRef(onClose);
  useEffect(() => { closeRef.current = onClose; });
  // × and Escape: nothing mid-request; else the form's own close (which is
  // onClose until its record exists).
  const requestClose = () => {
    if (busyRef.current) return;
    if (formRef.current) formRef.current.close();
    else closeRef.current?.();
  };
  const requestCloseRef = useRef(requestClose);
  useEffect(() => { requestCloseRef.current = requestClose; });
  useEffect(() => {
    if (!open) return undefined;
    focusElement(headingRef.current);
    const onKey = (e) => { if (e.key === 'Escape') requestCloseRef.current(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;
  // Unlike a quick action's, a click beside this drawer does not close it: a
  // whole intake (donor, place, tubes, photos) is too much to lose to a
  // stray tap. Escape, × and the form's Cancel still do.
  return (
    <div className="hub-drawer-backdrop" data-testid="intake-drawer-backdrop">
      <aside className="hub-drawer hub-drawer--wide" role="dialog" aria-modal="true" aria-labelledby="intake-drawer-title"
        onClick={(e) => e.stopPropagation()} data-testid="intake-drawer">
        <div className="hub-drawer__head" style={{ marginBottom: 12 }}>
          <h3 id="intake-drawer-title" ref={headingRef} tabIndex={-1} style={{ margin: 0, fontSize: 17 }}>
            {t('action.newIntake')}
          </h3>
          <button type="button" onClick={requestClose} disabled={busy}
            title={busy ? t('external:source.tip.closeBusy') : t('tip.closeDrawer')} aria-label={t('tip.closeDrawer')}
            data-testid="intake-drawer-close"
            style={{ background: 'none', border: 'none', fontSize: 22, cursor: busy ? 'not-allowed' : 'pointer',
              color: 'var(--c-text-muted-fg)', lineHeight: 1, opacity: busy ? 0.5 : 1 }}>
            ×
          </button>
        </div>
        <IntakeForm ref={formRef} user={user} protocols={protocols} compact onDone={onDone} onCancel={onClose}
          onBusyChange={onBusyChange} onSettled={onSettled} />
      </aside>
    </div>
  );
}
